// src/components/ExerciseDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Se cambia Link por useNavigate
import { IoArrowBack } from 'react-icons/io5';
import api from '../api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ExerciseDetail = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate(); // Se inicializa el hook para navegar
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('description');

  useEffect(() => {
    const fetchExercise = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/admin/exercises/${exerciseId}`);
        setExercise(response.data);
      } catch (err) {
        setError("Error al cargar el ejercicio.");
      } finally {
        setLoading(false);
      }
    };
    fetchExercise();
  }, [exerciseId]);

  // Nueva función que navega una página hacia atrás en el historial
  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) return <p className="text-white text-center py-10">Cargando...</p>;
  if (error) return <p className="text-red-500 text-center py-10">{error}</p>;
  if (!exercise) return <p className="text-white text-center py-10">Ejercicio no encontrado.</p>;

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white">
      {/* Se reemplaza el <Link> por un <button> que usa la nueva función */}
      <button 
        onClick={handleGoBack} 
        className="inline-flex items-center text-gray-300 hover:text-white mb-8"
      >
        <IoArrowBack className="mr-2" />
        Volver
      </button>
      
      <div className="bg-black bg-opacity-40 p-6 md:p-8 rounded-lg shadow-xl border border-gray-700">
        <h1 className="text-3xl md:text-4xl font-bold">{exercise.name}</h1>
        <p className="text-lg text-gray-400 mb-6">{exercise.category}</p>

        <div className="border-b border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('description')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'description' ? 'border-marizan-red text-marizan-red' : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              Descripción
            </button>
            <button
              onClick={() => setActiveTab('video')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'video' ? 'border-marizan-red text-marizan-red' : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              Video
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'images' ? 'border-marizan-red text-marizan-red' : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              Imágenes
            </button>
          </nav>
        </div>

        <div>
          {activeTab === 'description' && (
            <div className="prose prose-invert max-w-none text-gray-300"> 
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {exercise.description || 'No hay descripción disponible.'}
              </ReactMarkdown>
            </div>
          )}

          {activeTab === 'video' && (
            <div>
              {exercise.video_url ? (() => {
                let videoId = null;
                try {
                  const url = new URL(exercise.video_url);
                  if (url.hostname === 'youtu.be') {
                    videoId = url.pathname.substring(1);
                  } else {
                    videoId = url.searchParams.get('v');
                  }
                } catch (e) {
                  console.error("URL de video no válida:", exercise.video_url);
                }
                if (videoId) {
                  const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                  return (
                    <div className="aspect-w-16 aspect-h-9 w-full max-w-2xl mx-auto">
                      <iframe
                        className="w-full h-full"
                        src={embedUrl}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  );
                } else {
                  return <p className="text-red-500">La URL del video no es válida.</p>;
                }
              })() : (
                <p className="text-gray-400">No hay video disponible para este ejercicio.</p>
              )}
            </div>
          )}

          {activeTab === 'images' && (
            <div>
              {exercise.images && exercise.images.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exercise.images.map(img => (
                    <div key={img.id} className="overflow-hidden rounded-lg">
                      <img src={img.url} alt={`Imagen de ${exercise.name}`} className="w-full h-full object-cover"/>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">No hay imágenes disponibles para este ejercicio.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseDetail;